/**
 * Outil « Archimède : approcher π » (Boîte à outils, 5e → prépa).
 *
 * Un polygone régulier à n côtés est inscrit dans un cercle de rayon 1. Le
 * curseur fait passer n de 3 à 1000 : le polygone épouse le cercle et son
 * périmètre Pₙ monte vers 2π ; Pₙ ÷ 2 donne les décimales de π une à une.
 * « Lancer » déroule n tout seul. Le polygone circonscrit (au choix) encadre π
 * par au-dessus, comme Archimède avec 96 côtés. Calculs : `pi-polygon.ts`.
 */

import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, ParamSlider, PRIMARY_BUTTON, ToggleChip } from '../../ui/controls';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import {
  ARCHIMEDES_STEPS,
  circumscribedVertices,
  correctDecimals,
  nOfSlider,
  perimeterCircumscribed,
  perimeterInscribed,
  polygonVertices,
  sideInscribed,
  sliderOfN,
  truncText,
} from './pi-polygon';

const SIZE = 320;
const R = 120;
const C = SIZE / 2;
/** durée du déroulé de 3 à 1000 côtés */
const PLAY_MS = 9000;
const POLY = 'var(--color-primary-500)';
const OUTER = 'var(--color-accent-700)';
const PI_TEXT = '3,141592653…';

const toSvg = (pts: [number, number][]) =>
  pts.map(([x, y]) => `${(C + R * x).toFixed(2)},${(C - R * y).toFixed(2)}`).join(' ');

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** « 3,1415 » avec les décimales justes en vert */
function PiDigits({ value, digits = 6 }: { value: number; digits?: number }) {
  const text = truncText(value, digits);
  const ok = correctDecimals(value);
  const good = text.slice(0, ok > 0 ? 2 + ok : 1);
  return (
    <>
      <span className="text-success-600 font-bold">{good}</span>
      <span className="text-ink-700">{text.slice(good.length)}…</span>
    </>
  );
}

export function PiPolygonTool({ level }: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  // le sinus d'un angle quelconque arrive en 3e
  const trigKnown = rank === null || rank >= 4;
  const [t, setT] = useState(sliderOfN(6));
  const [outer, setOuter] = useState(false);
  const [playing, setPlaying] = useState(false);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (!playing) return;
    if (reducedMotion()) {
      setT(1);
      setPlaying(false);
      return;
    }
    const start = performance.now();
    const from = t >= 1 ? 0 : t;
    const tick = (now: number) => {
      const next = from + ((now - start) / PLAY_MS) * (1 - 0);
      if (next >= 1) {
        setT(1);
        setPlaying(false);
        return;
      }
      setT(next);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
    // le déroulé part de la position au moment du clic
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  const n = nOfSlider(t);
  const p = perimeterInscribed(n);
  const q = perimeterCircumscribed(n);
  const gap = 2 * Math.PI - p;
  const ok = correctDecimals(p / 2);

  return (
    <div className="space-y-4">
      <p className="text-ink-700 text-sm">
        Un polygone régulier à n côtés est tracé dans un cercle de rayon 1. Le périmètre du cercle
        vaut 2π. Plus n est grand, plus le polygone épouse le cercle.
      </p>

      <ParamSlider
        name="n (nombre de côtés)"
        ariaLabel="Nombre de côtés"
        value={t}
        min={0}
        max={1}
        step={0.001}
        format={() => String(n)}
        onChange={(v) => {
          setPlaying(false);
          setT(v);
        }}
      />
      <div className="flex flex-wrap gap-1" role="group" aria-label="Doublements d’Archimède">
        {ARCHIMEDES_STEPS.map((s) => (
          <ToggleChip
            key={s}
            on={n === s}
            onClick={() => {
              setPlaying(false);
              setT(sliderOfN(s));
            }}
          >
            {s}
          </ToggleChip>
        ))}
        <ToggleChip
          on={n === 1000}
          onClick={() => {
            setPlaying(false);
            setT(1);
          }}
        >
          1000
        </ToggleChip>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={cn(PRIMARY_BUTTON, 'inline-flex items-center gap-2')}
          onClick={() => setPlaying((v) => !v)}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {playing ? 'Pause' : 'Lancer de 3 à 1000'}
        </button>
        <ToggleChip on={outer} onClick={() => setOuter((v) => !v)}>
          Polygone extérieur
        </ToggleChip>
      </div>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={`Polygone régulier à ${n} côtés inscrit dans un cercle de rayon 1.`}
          data-testid="pi-figure"
        >
          <circle
            cx={C}
            cy={C}
            r={R}
            fill="none"
            stroke="var(--color-ink-400)"
            strokeDasharray="5 4"
            strokeWidth={1.5}
          />
          <line x1={C} y1={C} x2={C} y2={C - R} stroke="var(--color-ink-500)" strokeWidth={1.5} />
          <text x={C + 6} y={C - R / 2} fontSize={13} fill="var(--color-ink-600)">
            1
          </text>
          {outer && (
            <polygon
              points={toSvg(circumscribedVertices(n))}
              fill="none"
              stroke={OUTER}
              strokeWidth={1.5}
              strokeLinejoin="round"
              data-testid="pi-outer"
            />
          )}
          <polygon
            points={toSvg(polygonVertices(n))}
            fill="color-mix(in srgb, var(--color-primary-500) 12%, transparent)"
            stroke={POLY}
            strokeWidth={2.5}
            strokeLinejoin="round"
            data-testid="pi-polygon"
          />
          {n <= 48 &&
            polygonVertices(n).map(([x, y], i) => (
              <circle key={i} cx={C + R * x} cy={C - R * y} r={n <= 12 ? 4 : 2.5} fill={POLY} />
            ))}
          <circle cx={C} cy={C} r={3} fill="var(--color-ink-700)" />
        </svg>
      </div>

      <section
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-3"
        data-testid="pi-values"
      >
        {trigKnown && (
          <MathText className="text-ink-900 block">
            {`$P_n = n \\times 2\\sin\\left(\\frac{180°}{n}\\right)$`}
          </MathText>
        )}
        <p className="text-ink-700 text-sm tabular-nums">
          Côté ≈ {truncText(sideInscribed(n), 6)} · Périmètre Pₙ ≈ {truncText(p, 6)}
        </p>
        <p className="text-ink-700 text-sm tabular-nums">
          2π ≈ 6,283185… : il manque encore {gap < 1e-4 ? truncText(gap, 7) : truncText(gap, 4)}
        </p>
        <p className="font-display text-xl tabular-nums" data-testid="pi-approx">
          <span className="text-ink-600 text-base">π ≈ Pₙ ÷ 2 = </span>
          <PiDigits value={p / 2} />
        </p>
        <p className="text-ink-600 text-sm">
          π = {PI_TEXT} :{' '}
          {ok === 0
            ? 'aucune décimale juste'
            : `${ok} décimale${ok > 1 ? 's' : ''} juste${ok > 1 ? 's' : ''}`}{' '}
          avec {n} côtés.
        </p>
        {outer && (
          <p className="text-sm tabular-nums" data-testid="pi-bracket">
            <span className="text-ink-700">Encadrement : </span>
            <span className="text-ink-900 font-semibold">
              {truncText(p / 2, 5)} &lt; π &lt; {truncText(Math.ceil((q / 2) * 1e5) / 1e5, 5)}
            </span>
          </p>
        )}
        <p className="sr-only" aria-live="polite">
          {playing ? '' : `${n} côtés : π vaut environ ${truncText(p / 2, 5)}.`}
        </p>
      </section>

      <Note live={false}>
        Vers 250 avant J.-C., Archimède part de l’hexagone (périmètre 6, donc π ≈ 3), double le
        nombre de côtés jusqu’à 96 et encadre π entre un polygone intérieur et un polygone extérieur
        : 3 + 10/71 &lt; π &lt; 3 + 1/7. Le périmètre du polygone ne dépasse jamais 2π, mais s’en
        approche autant qu’on veut.
      </Note>
    </div>
  );
}
